///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  isEqual,
  pickBy,
  transform,
  values,
  cloneDeep,
  pick,
  last,
  findKey,
  find,
  difference
} from "lodash";

import Modal from "../../Modal";
import SystemErrorModal from "../../SystemErrorModal";
import ForbiddenErrorModal from "../../ForbiddenErrorModal";
import Spinner from "../../icons/Spinner";
import getTeamContactType from "../../../helpers/getTeamContactType";
import {
  updateTeamMembers,
  addNewSubscriber,
  setNewSubscriber,
  removeSubscriber,
  handleSubscriberValidation,
  updateTeamMemberDetails,
  fetchBidTeam
} from "../../../slices/bidSummarySlice";
import TeamMemberAssignment from "./TeamMemberAssignment";
import messages from "../../../helpers/messages";

export const TeamEditModal = ({ closeModal, bidId, team }) => {
  const dispatch = useDispatch();
  const { modalOpen, isLoading, isError, isForbidden, teamMembers } =
    useSelector(state => state.bidSummary.teamEdit);

  const handleOnSave = () => {
    const newContacts = pickBy(cloneDeep(teamMembers), t => t && t.isUpdated && t.userId);
    const filteredContacts = {};
    
    Object.keys(newContacts).forEach(key => {
      if (key !== "directorOfCustomerSuccess" && key !== "opportunityLeader" && key !== "subscribers") {
        filteredContacts[key] = newContacts[key];
      }
    });
    
    const teamContactUpdateRequest = values(
      transform(filteredContacts, function (result, value, key) {
        value["bidId"] = parseInt(bidId);
        value["userName"] = value["fullName"];
        value["teamContactType"] = getTeamContactType(key);
        result[key] = pick(value, [
          "bidId",
          "userId",
          "userName",
          "teamContactType"
        ]);
      })
    );
    const deleteSubscriberList = teamMembers["subscribers"]
      .filter(n => n.isRemoved === true)
      .map(i => i.userId);
    const addSubscriberList = teamMembers["subscribers"]
      .filter(n => n.isNewSubscriber === true)
      .map(i => i.userId);
    const removeBidSubscriberRequest = difference(
      deleteSubscriberList,
      addSubscriberList
    );
    const addBidSubscriberRequest = difference(
      addSubscriberList,
      deleteSubscriberList
    );
    
    const opportunityLeaderUserId = teamMembers.opportunityLeader?.isUpdated
      ? teamMembers.opportunityLeader.userId
      : null;
    const directorOfCustomerSuccessUserId = teamMembers.directorOfCustomerSuccess?.isUpdated
      ? teamMembers.directorOfCustomerSuccess.userId
      : null;

    dispatch(
      updateTeamMemberDetails(
        teamContactUpdateRequest,
        removeBidSubscriberRequest,
        addBidSubscriberRequest,
        bidId,
        opportunityLeaderUserId,
        directorOfCustomerSuccessUserId
      )
    );
  };

  const teamMemberChange = (selected, value, field) => {
    if (selected && selected.userId !== value?.value) {
      dispatch(
        handleSubscriberValidation({ subscriber: { userId: value?.value } })
      );
      dispatch(
        updateTeamMembers({
          newContact: selected,
          field: field
        })
      );
    }
  };

  const subscriberChange = (selected, teamMember) => {
    if (selected && selected.userId !== teamMember.userId) {
      dispatch(
        handleSubscriberValidation({
          subscriber: { userId: teamMember.userId }
        })
      );
      dispatch(
        setNewSubscriber({ newSubscriber: selected, id: teamMember.id })
      );
    }
  };

  const onRemove = subscriber => {
    dispatch(removeSubscriber({ subscriber }));
    if (!subscriber.error) {
      dispatch(handleSubscriberValidation({ subscriber }));
    }
  };

  const handleNewSubscriber = () => {
    const lastAdded = last(teamMembers["subscribers"]);
    let newSubscriber = {
      id: lastAdded && lastAdded.id ? lastAdded.id + 1 : 1,
      userId: null,
      fullName: "",
      isNewSubscriber: true
    };
    dispatch(addNewSubscriber({ newSubscriber }));
  };

  const handleExit = () => {
    dispatch(fetchBidTeam(bidId));
    closeModal();
  };

  return (
    <>
      {isForbidden && (
        <ForbiddenErrorModal
          open={isForbidden}
          message={messages.noPermissionToEditTeam.defaultMessage}
          closeModal={handleExit}
        />
      )}
      {isError && (
        <SystemErrorModal
          open={isError}
          closeModal={handleExit}
          handleExit={handleExit}
          handleTryAgain={handleOnSave}
        />
      )}
      {teamMembers && (
        <Modal
          title={messages.teamEdit.defaultMessage}
          open={modalOpen}
          handleClose={closeModal}
          modalStyle={{ width: "450px" }}
          titleClass="modal__title_light uppercase"
          bodyClass="border-top-grey pt-24"
          closeButtonClass="modal__close-button mr-16 mt-16"
        >
          <p
            data-testid="team-edit-modal"
            className="font-size-16 text-dim-grey pb-6 px-24"
          >
            {messages.teamEditModalBodyMessage.defaultMessage}
          </p>
          <div className="scroll scroll-on-hover" style={{ width: 448 }}>
            <div
              className="grid px-24 pt-2"
              style={{ maxHeight: window.screen.height - 385, width: 440 }}
            >
              {teamMembers.salesRepresentative && (
                <TeamMemberAssignment
                  teamMember={teamMembers.salesRepresentative}
                  label={messages.salesRepresentative.defaultMessage}
                  field="salesRepresentative"
                  dataTestId="sales-rep"
                  onChange={teamMemberChange}
                />
              )}
              {teamMembers.truckloadPricing && (
                <TeamMemberAssignment
                  teamMember={teamMembers.truckloadPricing}
                  label={messages.tlPricing.defaultMessage}
                  field="truckloadPricing"
                  dataTestId="tl-pricing"
                  onChange={teamMemberChange}
                />
              )}
              {teamMembers.lessThanTruckloadPricing && (
                <TeamMemberAssignment
                  teamMember={teamMembers.lessThanTruckloadPricing}
                  label={messages.ltlPricing.defaultMessage}
                  field="lessThanTruckloadPricing"
                  dataTestId="ltl-pricing"
                  onChange={teamMemberChange}
                />
              )}
              {teamMembers.intermodalPricing && (
                <TeamMemberAssignment
                  teamMember={teamMembers.intermodalPricing}
                  label={messages.imdlPricing.defaultMessage}
                  field="intermodalPricing"
                  dataTestId="imdl-pricing"
                  onChange={teamMemberChange}
                />
              )}
              <TeamMemberAssignment
                teamMember={teamMembers.directorOfCustomerSuccess || { userId: null, fullName: "" }}
                label={messages.directorOfCustomerSuccess.defaultMessage}
                field="directorOfCustomerSuccess"
                dataTestId="director-customer-success"
                onChange={teamMemberChange}
              />
              <TeamMemberAssignment
                teamMember={teamMembers.opportunityLeader || { userId: null, fullName: "" }}
                label={messages.opportunityLeader.defaultMessage}
                field="opportunityLeader"
                dataTestId="opportunity-leader"
                onChange={teamMemberChange}
              />
              {teamMembers.subscribers &&
                teamMembers.subscribers
                  .filter(x => x.isRemoved !== true)
                  .map((s, index) => {
                    return (
                      <TeamMemberAssignment
                        key={"additional_user" + index}
                        teamMember={s}
                        label={messages.additionalUser.defaultMessage}
                        dataTestId={"subscriber_" + index}
                        showRemoveIcon={true}
                        disabled={s.isNewSubscriber ? false : true}
                        onChange={subscriberChange}
                        onRemove={() => onRemove(s)}
                      />
                    );
                  })}
            </div>
          </div>
          <div className="pos-relative one-whole bg-white pb-24 px-24 inline-block">
            <div>
              <button
                data-testid="add-another-button"
                className="text-button univers-67-bold-cond font-size-14 uppercase"
                onClick={() => handleNewSubscriber()}
              >
                {messages.addAnother.defaultMessage}
              </button>
            </div>
            <div className="mt-6">
              {isLoading ? (
                <button
                  data-testid="save-button"
                  className="primary-button fr short-button"
                >
                  <Spinner
                    className="spinner"
                    fill="#ffffff"
                    height="16px"
                    width="16px"
                  />
                </button>
              ) : (
                <button
                  data-testid="save-button"
                  className="primary-button fr cursor-pointer univers-67-cond short-button"
                  disabled={
                    findKey(teamMembers, ["error", true]) ||
                    find(teamMembers["subscribers"], ["error", true]) ||
                    teamMembers["subscribers"].some(x => x.userId === null) ||
                    isEqual(teamMembers, team)
                  }
                  onClick={handleOnSave}
                >
                  {messages.save.defaultMessage}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TeamEditModal;
